import type { DraftState, PrimerDiagnosisKind, SheetState, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { SampleConfigurationTypes } from '../../Generated/SampleConfigurationType.ts'
import { comparePrimer } from './primerComparison.ts'
import { countItems, sampleDisplayName, sampleNameOf } from './sampleLabels.ts'
import { hasKeyColumn } from './sheet/sheetMapping.ts'
import type { SheetMatch } from './sheet/sheetMatching.ts'
import { sheetMatchOf } from './sheet/sheetMatching.ts'
import { sampleStemAsWritten } from '../../Util/fileNames.ts'
import { describeUnreadableCoordinates } from './sheet/sheetImport.ts'
import { archiveRows, archiveSamples, checkArchive, controlSheetRows, isArchiveOrder } from './archive.ts'
import { plural } from '../../Util/plural.ts'
import { placedGroups } from './pools.ts'
import { groupWriteError } from './writeState.ts'
import { declaredPrimers } from './sheet/applyPlan.ts'
import { writtenEstimate } from './primerOrientation.ts'

export type WarningTopic = 'files' | 'pools' | 'primers' | 'metadata'

/** The id of the finding that collects every failed write. */
export const FAILED_WRITES = 'failed-writes'

/**
 * Something the review found. One root cause is one finding, however many samples share it. `warning`
 * means probably wrong and fixable by the lab; `info` belongs in the step's summary; `failure` is a
 * write that failed, which the issue list counts as a blocker.
 */
export type ReviewFinding = {
  id: string
  topic: WarningTopic
  level: 'warning' | 'info'
  text: string
  groupKeys?: string[]
  /** The affected samples by name, for the expanded view. */
  details?: string[]
  failure?: boolean
}

const DIAGNOSIS_TEXT: Partial<Record<PrimerDiagnosisKind, string>> = {
  OTHER_PRIMER: 'the reads carry another registered primer pair',
  ONE_PRIMER_MISSING: 'one declared primer does not match its reads',
  PRIMERS_ABSENT: 'the declared primers are not on the reads (trimmed already?)',
  PRIMERS_PRESENT: 'the primers are still on the reads, but the sample format says trimmed'
}

const relevantDiagnosis = (draft: DraftState, group: WizardSampleGroup): PrimerDiagnosisKind | undefined => {
  const kind = group.diagnosis?.diagnosis
  if (!kind) {
    return undefined
  }
  const trimmed = draft.settings ? SampleConfigurationTypes[draft.settings.sampleConfiguration.value].impliesTrimmed : false
  const relevant =
    kind === 'TAG_MISMATCH' ||
    kind === 'OTHER_PRIMER' ||
    kind === 'ONE_PRIMER_MISSING' ||
    (kind === 'PRIMERS_ABSENT' && !trimmed) ||
    (kind === 'PRIMERS_PRESENT' && trimmed)
  return relevant ? kind : undefined
}

/* a same-length tag one base off is most likely a typo or a read error, and not worth a warning */
const nearTag = (a: string, b: string) => a.length === b.length && [...a].filter((base, i) => base !== b[i]).length <= 1

const percent = (share: number) => `${Math.round(share * 100)}%`

const SHARED_PREFIX = 4

const normalizedName = (value: string) =>
  sampleStemAsWritten(value)
    .toLowerCase()
    .replace(/[\s._-]/g, '')

const keyValuesOf = (sheet: SheetState, index: number) =>
  [sheet.mapping.sampleName, sheet.mapping.sampleId, sheet.mapping.fileForward]
    .map((column) => (column ? sheet.rows[index]?.[column]?.trim() : undefined))
    .filter((value): value is string => !!value)

/** Names of removed items that have not come back: a sample dropped again is judged like any other. */
const stillRemovedNames = (draft: DraftState): string[] => {
  const present = new Set(
    placedGroups(draft)
      .flatMap((group) => [sampleNameOf(draft, group), draft.files[group.forward]?.name])
      .filter((name): name is string => !!name)
      .map(normalizedName)
  )
  return (draft.removedNames ?? []).filter((name) => !present.has(normalizedName(name)))
}

/** Rows naming an item the lab removed: unmatched on purpose. */
const rowsOfRemoved = (sheet: SheetState, rows: readonly number[], removedNames: readonly string[]): Set<number> => {
  const removed = new Set(removedNames.map(normalizedName))
  return new Set(rows.filter((index) => keyValuesOf(sheet, index).some((value) => removed.has(normalizedName(value)))))
}

/**
 * Sheet rows no file matched that look like this order's own samples: their key begins with the same
 * four characters as a matched row's or a file's sample name ("Marsh 04" next to "Marsh01_S1…").
 * Sheets often list several pools or orders, so rows named otherwise are just not used, not a mistake.
 */
export const looksLikeThisOrder = (sheet: SheetState, rows: readonly number[], ownNames: readonly string[]): number[] => {
  const normalized = normalizedName
  const keyValues = (index: number) => keyValuesOf(sheet, index)
  const own = new Set(
    ownNames
      .map(normalized)
      .filter((value) => value.length >= SHARED_PREFIX)
      .map((value) => value.slice(0, SHARED_PREFIX))
  )
  return rows.filter((index) => keyValues(index).some((value) => own.has(normalized(value).slice(0, SHARED_PREFIX))))
}

class Findings {
  readonly list: ReviewFinding[] = []

  constructor(private readonly draft: DraftState) {}

  /** One finding for a cause shared by several items. */
  aggregate(id: string, topic: WarningTopic, level: ReviewFinding['level'], groups: WizardSampleGroup[], text: string) {
    if (groups.length === 0) {
      return
    }
    this.list.push({
      id,
      topic,
      level,
      text: `${countItems(groups)}: ${text}`,
      groupKeys: groups.map((group) => group.key),
      details: groups.map((group) => sampleDisplayName(this.draft, group.key))
    })
  }

  /** Every write that failed, as one failure: the step lists each with its own retry. */
  failedWrites(groups: WizardSampleGroup[]) {
    const failed = groups.flatMap((group) => {
      const error = groupWriteError(group)
      return error ? [{ group, error }] : []
    })
    if (failed.length > 0) {
      this.list.push({
        id: FAILED_WRITES,
        topic: 'pools',
        level: 'warning',
        failure: true,
        text: `${countItems(failed.map(({ group }) => group))} not written into the order`,
        groupKeys: failed.map(({ group }) => group.key),
        details: failed.map(({ group, error }) => `${sampleDisplayName(this.draft, group.key)}: ${error}`)
      })
    }
  }

  add(finding: ReviewFinding) {
    this.list.push(finding)
  }
}

const unusedRows = (findings: Findings, sheet: SheetState, allRows: number[], ownNames: string[], removedNames: readonly string[] = []) => {
  const removed = rowsOfRemoved(sheet, allRows, removedNames)
  const rows = allRows.filter((index) => !removed.has(index))
  const ours = looksLikeThisOrder(sheet, rows, ownNames)
  if (removed.size > 0) {
    findings.add({
      id: 'sheet-rows-removed',
      topic: 'metadata',
      level: 'info',
      text: `${plural(removed.size, 'sheet row')} for removed samples not used`
    })
  }
  if (ours.length > 0) {
    findings.add({
      id: 'sheet-rows-unmatched',
      topic: 'metadata',
      level: 'warning',
      text: `${plural(ours.length, 'sheet row')} of this order ${ours.length === 1 ? 'matches' : 'match'} no file`,
      details: ours.map((index) => `row ${index + 2}`)
    })
  }
  if (rows.length > ours.length) {
    findings.add({
      id: 'sheet-rows-unused',
      topic: 'metadata',
      level: 'info',
      text: `${plural(rows.length - ours.length, 'sheet row')} not used`
    })
  }
}

/** Rows whose key fits several samples: never matched, since taking one would be a guess. */
const ambiguousRows = (findings: Findings, sheet: SheetState, match: SheetMatch) => {
  if (match.ambiguousRows.length === 0) {
    return
  }
  const count = match.ambiguousRows.length
  findings.add({
    id: 'sheet-rows-ambiguous',
    topic: 'metadata',
    level: 'warning',
    text: `${plural(count, 'sheet row')} ${count === 1 ? 'fits' : 'fit'} more than one file and ${count === 1 ? 'is' : 'are'} not used`,
    details: match.ambiguousRows.map((index) => {
      const key = keyValuesOf(sheet, index)[0]
      return `row ${index + 2}${key ? `: "${key}"` : ''} fits ${match.ambiguousFits[index] ?? 'several'} files`
    })
  })
}

const archiveFindings = (draft: DraftState, findings: Findings) => {
  const samplesByGroup = archiveSamples(draft)
  for (const group of placedGroups(draft)) {
    const archive = draft.files[group.forward]?.name ?? group.key
    const samples = samplesByGroup[group.key] ?? []
    const add = (id: string, text: string) =>
      findings.add({
        id: `archive-${id}-${group.key}`,
        topic: 'pools',
        level: 'warning',
        text: `${archive}: ${text}`,
        groupKeys: [group.key]
      })
    if (samples.length === 0) {
      add('empty', 'no samples defined for this archive; it would be processed as one sample')
      continue
    }
    if (samples.length === 1) {
      add('single', 'only one sample defined; an archive with one sample is not demultiplexed')
    }
    const check = checkArchive(group, samples)
    if (check.incomplete.length > 0) {
      add('incomplete', `${plural(check.incomplete.length, 'sample')} without both exact primers, which the pipeline needs to demultiplex`)
    }
    if (check.duplicates.length > 0) {
      add('duplicates', `tags used by more than one sample: ${check.duplicates.join(', ')}`)
    }
    if (check.missingInReads.length > 0) {
      add('missing', `sheet tags not found in the scanned reads: ${check.missingInReads.join(', ')}`)
    }
    if (check.undefinedInSheet.length > 0) {
      const share = check.undefinedInSheet.reduce((sum, tag) => sum + tag.share, 0)
      add('undefined', `frequent read tags not in the sheet carry ${percent(share)} of the reads`)
    }
  }
  findings.failedWrites(placedGroups(draft))
  if (draft.sheet) {
    const rows = archiveRows(draft).unmatchedRows
    const names = Object.values(archiveSamples(draft)).flatMap((samples) => samples.map((sample) => sample.name))
    unusedRows(findings, draft.sheet, rows, names, stillRemovedNames(draft))
  }
}

const sampleFindings = (draft: DraftState, findings: Findings) => {
  const sheet = draft.sheet && hasKeyColumn(draft.sheet.mapping) ? draft.sheet : undefined
  const match = sheet ? sheetMatchOf(draft) : undefined
  const placed = placedGroups(draft)
  const byDiagnosis = new Map<PrimerDiagnosisKind, WizardSampleGroup[]>()
  const tagMismatch: WizardSampleGroup[] = []
  const tagNear: WizardSampleGroup[] = []
  const untagged: WizardSampleGroup[] = []
  for (const group of placed) {
    const diagnosis = relevantDiagnosis(draft, group)
    const declared = declaredPrimers(draft, group)
    const estimate = writtenEstimate(draft, group)
    const sides = [comparePrimer(estimate?.forward, declared.forward), comparePrimer(estimate?.reverse, declared.reverse)]
    const mismatches = sides.flatMap((side) => (side.kind === 'tag-mismatch' ? [side] : []))
    if (diagnosis === 'TAG_MISMATCH' || mismatches.some((side) => !nearTag(side.readsTag, side.sheetTag))) {
      tagMismatch.push(group)
    } else if (mismatches.length > 0) {
      tagNear.push(group)
    } else if (sides.some((side) => side.kind === 'sheet-untagged')) {
      untagged.push(group)
    }
    if (diagnosis && diagnosis !== 'TAG_MISMATCH') {
      byDiagnosis.set(diagnosis, [...(byDiagnosis.get(diagnosis) ?? []), group])
    }
  }
  findings.failedWrites(placed)
  findings.aggregate('tag-mismatch', 'primers', 'warning', tagMismatch, 'the reads carry a different tag than the primers given')
  for (const [kind, affected] of byDiagnosis) {
    findings.aggregate(`diagnosis-${kind}`, 'primers', 'warning', affected, DIAGNOSIS_TEXT[kind] ?? kind)
  }
  findings.aggregate('tag-near', 'primers', 'info', tagNear, 'the tag given differs from the reads by one base')
  findings.aggregate('sheet-untagged', 'primers', 'info', untagged, 'the primers given carry no tag, the reads do')

  if (sheet && match) {
    const withoutRow = samplesWithoutSheetRow(draft)
    findings.aggregate('sheet-no-row', 'metadata', 'warning', withoutRow, 'no sheet row')
    const matchedNames = Object.values(match.byGroup).flatMap((index) =>
      [sheet.mapping.sampleName, sheet.mapping.sampleId].map((column) => (column ? sheet.rows[index]?.[column] : undefined))
    )
    const fileNames = placed.map((group) => draft.files[group.forward]?.name)
    ambiguousRows(findings, sheet, match)
    unusedRows(
      findings,
      sheet,
      match.unmatchedRows,
      [...matchedNames, ...fileNames].filter((value): value is string => !!value),
      stillRemovedNames(draft)
    )
  }
}

/** Samples (not controls) the sample sheet has no row for; empty without a sheet that names samples. */
export const samplesWithoutSheetRow = (draft: DraftState): WizardSampleGroup[] => {
  const match = draft.sheet && hasKeyColumn(draft.sheet.mapping) ? sheetMatchOf(draft) : undefined
  return match ? placedGroups(draft).filter((group) => match.byGroup[group.key] === undefined && group.itemType !== 'NEGATIVE_CONTROL') : []
}

/** What the review finds: warnings to look at, notes for the summary line, and failed writes. */
export const reviewWarnings = (draft: DraftState): ReviewFinding[] => {
  const findings = new Findings(draft)
  if (isArchiveOrder(draft)) {
    archiveFindings(draft, findings)
  } else {
    sampleFindings(draft, findings)
  }
  const coordinates = draft.sheet ? describeUnreadableCoordinates(draft.sheet, controlSheetRows(draft)) : undefined
  if (coordinates) {
    findings.add({ id: 'coordinates', topic: 'metadata', level: 'warning', text: coordinates })
  }
  return findings.list
}
